import {
  AccordionContent as Content,
  type AccordionContentProps,
  AccordionHeader,
  AccordionItem as Item,
  type AccordionItemProps,
  AccordionTrigger as Trigger,
  type AccordionTriggerProps,
  Root as AccordionBase,
} from '@radix-ui/react-accordion';
import React, { forwardRef, type SVGProps } from 'react';

import cn from '@/helper/cn';

const AccordionTrigger = forwardRef<
  HTMLButtonElement,
  AccordionTriggerProps & {
    Icon: (prop: SVGProps<SVGSVGElement>) => JSX.Element;
  }
>(({ children, className, Icon, ...props }, forwardedRef) => (
  <AccordionHeader className="flex py-2 ">
    <Trigger
      className={cn(
        'flex flex-1 items-center justify-between gap-4 text-start font-normal leading-none text-zinc-900 duration-300 [&>svg]:transition-transform [&>svg]:data-[state=open]:rotate-90',
        className,
      )}
      {...props}
      ref={forwardedRef}
    >
      {children}
      {Icon && <Icon aria-hidden />}
    </Trigger>
  </AccordionHeader>
));
AccordionTrigger.displayName = 'AccordionTrigger';

const AccordionContent = forwardRef<HTMLDivElement, AccordionContentProps>(
  ({ children, className, ...props }, forwardedRef) => (
    <Content
      className={cn(
        'overflow-hidden pr-4 font-light text-zinc-700 data-[state="closed"]:animate-accordion-close data-[state="open"]:animate-accordion-open md:translate-y-2 lg:pr-12',
        className,
      )}
      {...props}
      ref={forwardedRef}
    >
      {children}
    </Content>
  ),
);
AccordionContent.displayName = 'AccordionContent';

const AccordionItem = forwardRef<HTMLDivElement, AccordionItemProps>(
  ({ children, className, ...props }, forwardedRef) => (
    <Item
      className={cn(
        'border-text-zinc-200 overflow-hidden border-b py-2 focus-within:relative focus-within:z-10 md:py-5',
        className,
      )}
      {...props}
      ref={forwardedRef}
    >
      {children}
    </Item>
  ),
);
AccordionItem.displayName = 'AccordionItem';

export { AccordionBase, AccordionContent, AccordionItem, AccordionTrigger };
